import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { carpoolsAPI, usersAPI } from '../services/api';
import Modal from '../components/Modal';
import MapPicker from '../components/MapPicker';
import RouteMap from '../components/RouteMap';
import InviteModal from '../components/InviteModal';
import { PageLoading, PageHead, ActivityIconBadge } from '../components/appPage';
import { getActivityLabel } from '../utils/carpoolUi';
import { UserPlus, Users, Baby, MapPin, Navigation, CircleDot, ChevronDown, ChevronUp } from 'lucide-react';
import './CarpoolDetails.css';

const CarpoolDetails = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [carpool, setCarpool] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [showDeleteCarpoolModal, setShowDeleteCarpoolModal] = useState(false);
  const [showRemoveMemberModal, setShowRemoveMemberModal] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [pickupPoints, setPickupPoints] = useState({ carpool: [], defaults: [] });
  const [userChildren, setUserChildren] = useState([]);
  const [showPickupModal, setShowPickupModal] = useState(false);
  const [pickupForm, setPickupForm] = useState({
    childId: '',
    label: '',
    address: '',
    latitude: null,
    longitude: null,
    isDefault: false
  });
  const [editingPickup, setEditingPickup] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [activeTab, setActiveTab] = useState('members');

  useEffect(() => {
    loadCarpool();
    loadUserChildren();
    // Get current user ID from token
    const token = localStorage.getItem('accessToken');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setCurrentUserId(payload.userId);
      } catch (e) {
        console.error('Failed to parse token:', e);
      }
    }
  }, [id]);

  const loadCarpool = async () => {
    try {
      const response = await carpoolsAPI.getCarpool(id);
      setCarpool(response.data.carpool);
      await loadPickupPoints();
    } catch (error) {
      console.error('Failed to load carpool:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPickupPoints = async () => {
    try {
      const response = await carpoolsAPI.getPickupPoints(id);
      setPickupPoints(response.data.pickupPoints || { carpool: [], defaults: [] });
    } catch (error) {
      console.error('Failed to load pickup points:', error);
    }
  };

  const loadUserChildren = async () => {
    try {
      const response = await usersAPI.getChildren();
      setUserChildren(response.data.children || []);
    } catch (error) {
      console.error('Failed to load user children:', error);
    }
  };

  const handleLeaveCarpool = async () => {
    setActionLoading(true);
    try {
      await carpoolsAPI.leaveCarpool(id);
      navigate('/carpools');
    } catch (error) {
      setErrorMessage(error.response?.data?.error?.message || t('errors.generic'));
      setShowErrorModal(true);
      setShowLeaveModal(false);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteCarpool = async () => {
    setActionLoading(true);
    setShowDeleteCarpoolModal(false);
    try {
      await carpoolsAPI.deleteCarpool(id);
      navigate('/carpools');
    } catch (error) {
      setErrorMessage(error.response?.data?.error?.message || t('errors.generic'));
      setShowErrorModal(true);
    } finally {
      setActionLoading(false);
    }
  };

  const handleRemoveMember = async () => {
    if (!memberToRemove) return;
    setActionLoading(true);
    try {
      await carpoolsAPI.removeMember(id, memberToRemove.user.id);
      setShowRemoveMemberModal(false);
      setMemberToRemove(null);
      setSuccessMessage('Членът е премахнат успешно');
      setShowSuccessModal(true);
      await loadCarpool();
    } catch (error) {
      setErrorMessage(error.response?.data?.error?.message || t('errors.generic'));
      setShowErrorModal(true);
    } finally {
      setActionLoading(false);
    }
  };

  const openRemoveMemberModal = (member) => {
    setMemberToRemove(member);
    setShowRemoveMemberModal(true);
  };

  const resolveDefaultPickupForChild = (childId) => {
    if (!childId) return null;
    const defaults = pickupPoints.defaults || [];
    const forChild = defaults.filter((p) => p.child?.id === childId);
    if (forChild.length === 0) return null;
    return forChild.find((p) => p.isDefault) || forChild[0];
  };

  const buildPickupFormForChild = (childId, existingPoint = null) => {
    if (existingPoint) {
      return {
        childId: existingPoint.child?.id || childId || '',
        label: existingPoint.label || '',
        address: existingPoint.address || '',
        latitude:
          existingPoint.latitude != null ? Number(existingPoint.latitude) : null,
        longitude:
          existingPoint.longitude != null ? Number(existingPoint.longitude) : null,
        isDefault: Boolean(existingPoint.isDefault)
      };
    }
    const def = resolveDefaultPickupForChild(childId);
    const lat =
      def?.latitude != null
        ? Number(def.latitude)
        : carpool?.startLatitude != null
          ? Number(carpool.startLatitude)
          : null;
    const lng =
      def?.longitude != null
        ? Number(def.longitude)
        : carpool?.startLongitude != null
          ? Number(carpool.startLongitude)
          : null;
    return {
      childId: childId || '',
      label: def?.label || '',
      address: def?.address || '',
      latitude: lat,
      longitude: lng,
      isDefault: false
    };
  };

  const openPickupModal = (pickupPoint = null) => {
    const initialChildId = pickupPoint?.child?.id || userChildren[0]?.id || '';
    setPickupForm(
      pickupPoint
        ? buildPickupFormForChild(initialChildId, pickupPoint)
        : buildPickupFormForChild(initialChildId, null)
    );
    setEditingPickup(pickupPoint);
    setShowPickupModal(true);
  };

  const handlePickupSubmit = async (e) => {
    e.preventDefault();
    try {
      if (!pickupForm.childId) {
        setErrorMessage(t('carpools.selectChildRequired'));
        setShowErrorModal(true);
        return;
      }

      if (!pickupForm.latitude || !pickupForm.longitude) {
        setErrorMessage(t('carpools.pickupLocationRequired'));
        setShowErrorModal(true);
        return;
      }

      const payload = {
        childId: pickupForm.childId,
        label: pickupForm.label || undefined,
        address: pickupForm.address,
        latitude: pickupForm.latitude,
        longitude: pickupForm.longitude,
        isDefault: pickupForm.isDefault
      };

      if (editingPickup) {
        await carpoolsAPI.updatePickupPoint(id, editingPickup.id, payload);
      } else {
        await carpoolsAPI.createPickupPoint(id, payload);
      }

      await loadPickupPoints();
      setShowPickupModal(false);
      setShowSuccessModal(true);
      setSuccessMessage(editingPickup ? t('carpools.pickupUpdateSuccess') : t('carpools.pickupCreateSuccess'));
    } catch (error) {
      console.error('Pickup point error:', error);
      setErrorMessage(error.response?.data?.error?.message || t('errors.generic'));
      setShowErrorModal(true);
    }
  };

  const handlePickupDelete = async (pickupPointId) => {
    try {
      await carpoolsAPI.deletePickupPoint(id, pickupPointId);
      await loadPickupPoints();
      setSuccessMessage(t('carpools.pickupDeleteSuccess'));
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Delete pickup point error:', error);
      setErrorMessage(t('errors.generic'));
      setShowErrorModal(true);
    }
  };

  const hasLocationSupport = useMemo(() => Boolean(process.env.REACT_APP_GOOGLE_MAPS_API_KEY), []);
  const startLocation = useMemo(() => {
    if (!carpool?.startLatitude || !carpool?.startLongitude) return null;
    return { lat: Number(carpool.startLatitude), lng: Number(carpool.startLongitude) };
  }, [carpool]);
  const endLocation = useMemo(() => {
    if (!carpool?.endLatitude || !carpool?.endLongitude) return null;
    return { lat: Number(carpool.endLatitude), lng: Number(carpool.endLongitude) };
  }, [carpool]);

  const validWaypoints = useMemo(() => {
    return (pickupPoints?.carpool || [])
      .filter(p => p.latitude && p.longitude)
      .map(p => ({
        id: p.id,
        lat: Number(p.latitude),
        lng: Number(p.longitude),
        address: p.address,
        label: p.label,
        childName: p.child?.name || ''
      }));
  }, [pickupPoints]);

  const handleOpenNavigation = () => {
    if (!startLocation || !endLocation) return;
    
    const baseUrl = "https://www.google.com/maps/dir/?api=1";
    const originStr = `&origin=${startLocation.lat},${startLocation.lng}`;
    const destStr = `&destination=${endLocation.lat},${endLocation.lng}`;
    
    let waypointsStr = "";
    if (validWaypoints && validWaypoints.length > 0) {
      const wpCoords = validWaypoints.map(wp => `${wp.lat},${wp.lng}`).join('|');
      waypointsStr = `&waypoints=${wpCoords}`;
    }
    
    const finalUrl = `${baseUrl}${originStr}${destStr}${waypointsStr}&travelmode=driving`;
    window.open(finalUrl, '_blank');
  };

  if (loading) {
    return (
      <div className="carpool-details-page page-shell">
        <PageLoading message={t('common.loading')} />
      </div>
    );
  }

  if (!carpool) {
    return (
      <div className="carpool-details-page page-shell">
        <div className="empty-state">
          <h3>Carpool not found</h3>
        </div>
      </div>
    );
  }

  const memberCount = carpool.members?.length || 0;
  const childCount = pickupPoints.carpool?.length || 0;
  const pickupCount = pickupPoints.carpool?.length || 0;
  const isCreator = carpool.createdBy === currentUserId;

  return (
    <div className="carpool-details-page page-shell">
      <PageHead
        title={carpool.name}
        lead={
          <span className="page-head__activity-line">
            <ActivityIconBadge activity={carpool.activity} size={22} />
            <span className="page-head__activity-label">{getActivityLabel(carpool.activity, t)}</span>
          </span>
        }
        actions={
          <button
            type="button"
            className="page-btn page-btn--primary"
            onClick={() => setShowInviteModal(true)}
          >
            <UserPlus size={18} strokeWidth={2} aria-hidden />
            {t('invitations.sendInvitation')}
          </button>
        }
      />

      <div className="route-overview-modern">
        <div className="navigation-timeline-card">
          
          <div className="timeline-nodes">
            {/* Start Node */}
            <div className="timeline-node">
              <div className="node-icon-wrapper start">
                <CircleDot size={20} strokeWidth={2.5} />
              </div>
              <div className="node-content">
                <span className="node-label">{t('carpools.startPoint')}</span>
                <h3 className="node-address">{carpool.startAddress || carpool.address || '—'}</h3>
              </div>
            </div>

            {/* Connecting Line */}
            <div className="timeline-connection">
              <div className="animated-line"></div>
            </div>

            {/* Waypoints */}
            {validWaypoints.map((wp, i) => (
              <div key={wp.id || i} className="timeline-node">
                <div className="node-icon-wrapper waypoint">
                  <Baby size={20} strokeWidth={2} />
                </div>
                <div className="node-content">
                  <span className="node-label">Пикап: {wp.childName || 'Дете'}</span>
                  <h3 className="node-address" style={{ fontSize: '15px' }}>{wp.address || wp.label}</h3>
                </div>
              </div>
            ))}

            {/* End Node */}
            <div className="timeline-node">
              <div className="node-icon-wrapper end">
                <MapPin size={20} strokeWidth={2.5} />
              </div>
              <div className="node-content">
                <span className="node-label">{t('carpools.endPoint')}</span>
                <h3 className="node-address">{carpool.endAddress || '—'}</h3>
              </div>
            </div>
          </div>

          <div className="maps-container" style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
             <details className="map-disclosure" style={{ flex: 1 }}>
               <summary className="map-disclosure-summary">
                 <MapPin size={16} /> 
                 <span>Виж на картата</span>
               </summary>
               <div className="disclosure-maps-grid single-map-layout">
                 <div className="small-map single-route-map">
                   <RouteMap origin={startLocation} destination={endLocation} waypoints={validWaypoints} />
                 </div>
               </div>
             </details>

             <button 
               onClick={handleOpenNavigation}
               style={{ 
                 height: '42px',
                 padding: '0 16px',
                 borderRadius: '12px',
                 display: 'flex',
                 alignItems: 'center',
                 gap: '8px',
                 whiteSpace: 'nowrap',
                 background: '#2563eb',
                 boxShadow: '0 2px 8px rgba(37, 99, 235, 0.25)',
                 border: 'none',
                 color: 'white',
                 fontWeight: '600',
                 cursor: 'pointer',
                 fontSize: '14px',
                 transition: 'all 0.2s'
               }}
               onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-1px)'}
               onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
             >
               <Navigation size={16} fill="currentColor" />
               Навигация
             </button>
          </div>

        </div>
      </div>

      {/* Carpool Full Banner - based on children vs seats per car */}
      {(pickupPoints.carpool?.length || 0) >= (carpool.childSeatsPerCar || 3) && (
        <div className="carpool-full-banner">
          <div className="full-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
          </div>
          <div className="full-text">
            <strong>Местата са запълнени</strong>
            <span>{pickupPoints.carpool?.length || 0} от {carpool.childSeatsPerCar || 3} места</span>
          </div>
        </div>
      )}

      <div className="carpool-tabs" role="tablist" aria-label="Carpool sections">
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'members'}
          className={`carpool-tab ${activeTab === 'members' ? 'active' : ''}`}
          onClick={() => setActiveTab('members')}
        >
          <Users size={16} strokeWidth={2} aria-hidden />
          {t('carpools.members')}
          <span className="carpool-tab__count">{memberCount}</span>
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'children'}
          className={`carpool-tab ${activeTab === 'children' ? 'active' : ''}`}
          onClick={() => setActiveTab('children')}
        >
          <Baby size={16} strokeWidth={2} aria-hidden />
          Деца
          <span className="carpool-tab__count">{childCount}</span>
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'pickups'}
          className={`carpool-tab ${activeTab === 'pickups' ? 'active' : ''}`}
          onClick={() => setActiveTab('pickups')}
        >
          <MapPin size={16} strokeWidth={2} aria-hidden />
          {t('carpools.pickupPoints')}
          <span className="carpool-tab__count">{pickupCount}</span>
        </button>
      </div>

      {activeTab === 'members' && <div className="carpool-section">
        <div className="section-header">
          <h2 className="section-title">{t('carpools.members')}</h2>
          <span className="member-count">{carpool.members?.length || 0}/{carpool.maxMembers || 10}</span>
        </div>
        <div className="members-grid">
          {carpool.members?.map(member => (
            <div key={member.id} className="member-card">
              <div className="member-avatar">
                {member.user.profilePhoto ? (
                  <img src={member.user.profilePhoto} alt={member.user.firstName} />
                ) : (
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                    <circle cx="12" cy="7" r="4"></circle>
                  </svg>
                )}
              </div>
              <div className="member-info">
                <h3 className="member-name">
                  {member.user.firstName} {member.user.lastName}
                  {member.user.id === currentUserId && <span className="you-badge">(ти)</span>}
                </h3>
                <span className="member-joined">
                  Присъединен {new Date(member.joinedAt).toLocaleDateString('bg-BG')}
                </span>
              </div>
              {member.user.id !== currentUserId && (
                <button
                  className="member-remove-btn"
                  onClick={() => openRemoveMemberModal(member)}
                  title="Премахни член"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              )}
            </div>
          ))}
        </div>
      </div>}

      {/* Children Section - All children in carpool grouped by parent */}
      {activeTab === 'children' && <div className="carpool-section children-section">
        <div className="section-header">
          <h2 className="section-title">Деца в карпула</h2>
          <span className="children-count">
            {pickupPoints.carpool?.length || 0} {pickupPoints.carpool?.length === 1 ? 'дете' : 'деца'}
          </span>
        </div>

        {(() => {
          // Group children by parent
          const childrenByParent = {};
          pickupPoints.carpool?.forEach(point => {
            if (point.child) {
              const parentId = point.child.userId;
              const parentName = point.child.user
                ? `${point.child.user.firstName} ${point.child.user.lastName}`
                : 'Неизвестен';

              if (!childrenByParent[parentId]) {
                childrenByParent[parentId] = {
                  parentName,
                  parentId,
                  children: []
                };
              }
              // Avoid duplicates
              if (!childrenByParent[parentId].children.find(c => c.id === point.child.id)) {
                childrenByParent[parentId].children.push(point.child);
              }
            }
          });

          const childrenInCarpool = pickupPoints.carpool?.map(p => p.child?.id).filter(Boolean) || [];
          const childrenNotInCarpool = userChildren.filter(c => !childrenInCarpool.includes(c.id));

          return (
            <>
              {/* All children grouped by parent */}
              {Object.keys(childrenByParent).length > 0 ? (
                <div className="parents-children-list">
                  {Object.values(childrenByParent).map(({ parentId, parentName, children }) => (
                    <div key={parentId} className={`parent-group ${parentId === currentUserId ? 'my-children' : ''}`}>
                      <div className="parent-header">
                        <div className="parent-avatar">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                            <circle cx="12" cy="7" r="4"></circle>
                          </svg>
                        </div>
                        <span className="parent-name">
                          {parentId === currentUserId ? 'Моите деца' : parentName}
                        </span>
                        <span className="children-badge">{children.length}</span>
                      </div>
                      <div className="children-list">
                        {children.map(child => (
                          <div key={child.id} className="child-card">
                            <div className="child-avatar">
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                                <circle cx="12" cy="7" r="4"></circle>
                              </svg>
                            </div>
                            <div className="child-info">
                              <span className="child-name">{child.name}</span>
                              {child.age && <span className="child-age">{child.age} години</span>}
                            </div>
                            <div className="child-status">
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                <polyline points="20 6 9 17 4 12"></polyline>
                              </svg>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="no-children-yet">
                  <p>Все още няма добавени деца в карпула.</p>
                </div>
              )}

              {/* Add my children section */}
              {childrenNotInCarpool.length > 0 && (
                <div className="add-children-section">
                  <div className="add-children-header">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"></circle>
                      <line x1="12" y1="8" x2="12" y2="16"></line>
                      <line x1="8" y1="12" x2="16" y2="12"></line>
                    </svg>
                    <span>Добави дете</span>
                  </div>
                  <div className="add-children-list">
                    {childrenNotInCarpool.map(child => (
                      <button
                        key={child.id}
                        className="add-child-btn"
                        onClick={() => {
                          setPickupForm({
                            childId: child.id,
                            label: 'Вкъщи',
                            address: '',
                            latitude: carpool?.startLatitude || null,
                            longitude: carpool?.startLongitude || null,
                            isDefault: true
                          });
                          setEditingPickup(null);
                          setShowPickupModal(true);
                        }}
                      >
                        <div className="add-child-avatar">
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                            <circle cx="12" cy="7" r="4"></circle>
                          </svg>
                        </div>
                        <span className="add-child-name">{child.name}</span>
                        <div className="add-child-icon">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                            <line x1="12" y1="5" x2="12" y2="19"></line>
                            <line x1="5" y1="12" x2="19" y2="12"></line>
                          </svg>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {userChildren.length === 0 && (
                <div className="no-children-notice">
                  <p>Нямате добавени деца в профила си.</p>
                  <Link to="/children" className="btn-secondary">
                    Добави дете
                  </Link>
                </div>
              )}
            </>
          );
        })()}
      </div>}

      <div className="carpool-actions">
        <Link to={`/schedule/${carpool.id}`} className="btn-primary btn-lg">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
            <line x1="16" y1="2" x2="16" y2="6"></line>
            <line x1="8" y1="2" x2="8" y2="6"></line>
            <line x1="3" y1="10" x2="21" y2="10"></line>
          </svg>
          {t('schedule.title')}
        </Link>
        <Link to={`/tracking/${carpool.id}`} className="btn-secondary btn-lg">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
            <circle cx="12" cy="10" r="3"></circle>
          </svg>
          {t('tracking.title')}
        </Link>
        {!isCreator && <button
          className="btn-danger btn-lg"
          onClick={() => setShowLeaveModal(true)}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
            <polyline points="16 17 21 12 16 7"></polyline>
            <line x1="21" y1="12" x2="9" y2="12"></line>
          </svg>
          Напусни карпула
        </button>}
        {isCreator && <button
          className="btn-danger btn-lg"
          onClick={() => setShowDeleteCarpoolModal(true)}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="3 6 5 6 21 6"></polyline>
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
          </svg>
          Изтрий карпула
        </button>}
      </div>

      {activeTab === 'pickups' && <div className="carpool-section">
        <div className="section-header">
          <h2 className="section-title">{t('carpools.pickupPoints')}</h2>
          <button
            className="btn-secondary"
            onClick={() => openPickupModal()}
            disabled={userChildren.length === 0}
          >
            + {t('carpools.addPickupPoint')}
          </button>
        </div>

        {pickupPoints.carpool.length === 0 ? (
          <div className="pickup-empty-state">
            <p>{t('carpools.noPickupPoints')}</p>
          </div>
        ) : (
          <div className="pickup-table-wrapper">
            <table className="pickup-table">
              <thead>
                <tr>
                  <th>{t('common.child')}</th>
                  <th>{t('children.pickupLabel')}</th>
                  <th>{t('children.pickupAddress')}</th>
                  <th>{t('children.default')}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {pickupPoints.carpool.map(point => (
                  <tr key={point.id}>
                    <td>{point.child?.name || '—'}</td>
                    <td>{point.label || t('children.pickupNoLabel')}</td>
                    <td>{point.address}</td>
                    <td>{point.isDefault ? t('common.yes') : t('common.no')}</td>
                    <td className="pickup-actions-cell">
                      <button className="btn-link" onClick={() => openPickupModal(point)}>{t('common.edit')}</button>
                      <button className="btn-link danger" onClick={() => handlePickupDelete(point.id)}>{t('common.delete')}</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {pickupPoints.defaults.length > 0 && (
          <div className="pickup-defaults">
            <h3>{t('carpools.defaultPickupPoints')}</h3>
            <ul>
              {pickupPoints.defaults.map(point => (
                <li key={point.id}>
                  <span>{point.label || t('children.pickupNoLabel')} • {point.address}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>}

      {/* Invite Modal */}
      <InviteModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        carpoolId={id}
        carpoolName={carpool.name}
        onInviteSent={loadCarpool}
      />

      {/* Leave Carpool Modal */}
      <Modal
        isOpen={showLeaveModal}
        onClose={() => setShowLeaveModal(false)}
        title="Напускане на карпула"
        size="small"
      >
        <div className="confirm-modal-content">
          <div className="confirm-modal-icon" style={{ background: '#fef2f2', color: '#dc2626' }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
              <polyline points="16 17 21 12 16 7"></polyline>
              <line x1="21" y1="12" x2="9" y2="12"></line>
            </svg>
          </div>
          <p className="confirm-modal-description">
            Сигурни ли сте, че искате да напуснете карпула "{carpool.name}"?
          </p>
          <div className="modal-footer">
            <button
              className="btn-secondary"
              onClick={() => setShowLeaveModal(false)}
              disabled={actionLoading}
            >
              Отказ
            </button>
            <button
              className="btn-danger"
              onClick={handleLeaveCarpool}
              disabled={actionLoading}
            >
              {actionLoading ? 'Напускане...' : 'Напусни'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Delete Carpool Modal */}
      <Modal
        isOpen={showDeleteCarpoolModal}
        onClose={() => setShowDeleteCarpoolModal(false)}
        title="Изтриване на карпула"
        size="small"
      >
        <div className="confirm-modal-content">
          <div className="confirm-modal-icon" style={{ background: '#fee2e2', color: '#dc2626' }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="3 6 5 6 21 6"></polyline>
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
            </svg>
          </div>
          <p className="confirm-modal-description">
            Сигурни ли сте, че искате да изтриете карпула? Това действие е необратимо.
          </p>
          <div className="modal-footer">
            <button className="btn-secondary" onClick={() => setShowDeleteCarpoolModal(false)}>
              Отказ
            </button>
            <button className="btn-danger" onClick={handleDeleteCarpool} disabled={actionLoading}>
              {actionLoading ? 'Изтриване...' : 'Изтрий карпула'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Remove Member Modal */}
      <Modal
        isOpen={showRemoveMemberModal}
        onClose={() => setShowRemoveMemberModal(false)}
        title="Премахване на член"
        size="small"
      >
        <div className="confirm-modal-content">
          <div className="confirm-modal-icon" style={{ background: '#fef2f2', color: '#dc2626' }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
              <circle cx="8.5" cy="7" r="4"></circle>
              <line x1="18" y1="8" x2="23" y2="13"></line>
              <line x1="23" y1="8" x2="18" y2="13"></line>
            </svg>
          </div>
          <p className="confirm-modal-description">
            Сигурни ли сте, че искате да премахнете{' '}
            <strong>{memberToRemove?.user?.firstName} {memberToRemove?.user?.lastName}</strong>{' '}
            от карпула?
          </p>
          <div className="modal-footer">
            <button
              className="btn-secondary"
              onClick={() => setShowRemoveMemberModal(false)}
              disabled={actionLoading}
            >
              Отказ
            </button>
            <button
              className="btn-danger"
              onClick={handleRemoveMember}
              disabled={actionLoading}
            >
              {actionLoading ? 'Премахване...' : 'Премахни'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Success Modal */}
      <Modal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        title={t('common.success')}
        size="small"
      >
        <div className="confirm-modal-content">
          <div className="confirm-modal-icon" style={{ background: '#d1fae5', color: '#10b981' }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
          </div>
          <p className="confirm-modal-description">{successMessage || t('common.success')}</p>
          <div className="modal-footer">
            <button type="button" className="btn-primary" onClick={() => setShowSuccessModal(false)}>
              {t('common.ok')}
            </button>
          </div>
        </div>
      </Modal>

      {/* Pickup Modal */}
      <Modal
        isOpen={showPickupModal}
        onClose={() => setShowPickupModal(false)}
        title={editingPickup ? t('carpools.editPickupPoint') : t('carpools.addPickupPoint')}
        size="medium"
      >
        <form className="pickup-form" onSubmit={handlePickupSubmit}>
          <div className="form-group">
            <label>{t('common.child')}</label>
            <select
              className="form-input"
              value={pickupForm.childId}
              onChange={(e) => {
                const newChildId = e.target.value;
                if (editingPickup) {
                  setPickupForm((prev) => ({ ...prev, childId: newChildId }));
                  return;
                }
                setPickupForm(buildPickupFormForChild(newChildId, null));
              }}
              required
            >
              {userChildren.map(child => (
                <option key={child.id} value={child.id}>{child.name}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>{t('children.pickupLabel')}</label>
            <input
              type="text"
              className="form-input"
              value={pickupForm.label}
              onChange={(e) => setPickupForm({ ...pickupForm, label: e.target.value })}
              placeholder={t('children.pickupLabelPlaceholder')}
            />
          </div>
          <div className="form-group">
            <label>{t('children.pickupAddress')}</label>
            {hasLocationSupport ? (
              <div className="selected-address">
                {pickupForm.address ? pickupForm.address : t('children.mapAddressPlaceholder')}
              </div>
            ) : (
              <input
                type="text"
                className="form-input"
                required
                value={pickupForm.address}
                onChange={(e) => setPickupForm((prev) => ({ ...prev, address: e.target.value }))}
                placeholder={t('children.pickupAddressPlaceholder')}
              />
            )}
          </div>
          <MapPicker
            value={pickupForm.latitude && pickupForm.longitude ? { lat: pickupForm.latitude, lng: pickupForm.longitude } : null}
            onChange={(coords) => setPickupForm((prev) => ({ ...prev, latitude: coords.lat, longitude: coords.lng }))}
            addressValue={pickupForm.address}
            onAddressChange={(address) => setPickupForm((prev) => ({ ...prev, address }))}
            helperText={t('children.pickupMapHelp')}
            disabled={!hasLocationSupport}
          />
          <div className="form-group checkbox">
            <label>
              <input
                type="checkbox"
                checked={pickupForm.isDefault}
                onChange={(e) => setPickupForm({ ...pickupForm, isDefault: e.target.checked })}
              />
              {t('children.setAsDefault')}
            </label>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn-secondary" onClick={() => setShowPickupModal(false)}>
              {t('common.cancel')}
            </button>
            <button type="submit" className="btn-primary">
              {t('common.save')}
            </button>
          </div>
        </form>
      </Modal>

      {/* Error Modal */}
      <Modal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        title={t('errors.error')}
        size="small"
      >
        <div className="confirm-modal-content">
          <div className="confirm-modal-icon">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
          </div>
          <p className="confirm-modal-description">{errorMessage}</p>
          <div className="modal-footer">
            <button type="button" className="btn-primary" onClick={() => setShowErrorModal(false)}>
              {t('common.ok')}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default CarpoolDetails;
